/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import type { Images } from "@packages/sdk";
import type { JSX, ReactNode } from "react";
import { forwardRef } from "react";

import {
  colors,
  constantSemanticColors,
  radius,
  spacing,
} from "../../../../global/emotion";
import { stylesOpacity } from "../../../../global/emotion/styles/stylesOpacity";
import { stylesOutline } from "../../../../global/emotion/styles/stylesOutline";
import { stylesScale } from "../../../../global/emotion/styles/stylesScale";
import { useThemeValues } from "../../../hooks/useThemeValues";
import type { HallowElement } from "../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../types/keyedElement.types";
import type { WithAsChild } from "../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../utils/determineElementFromAsChild";
import type { MaskProps } from "../../_base/_core/Mask/Mask.emotion";
import { Mask } from "../../_base/_core/Mask/Mask.emotion";
import { Tag } from "../../_base/_core/Tag/Tag.emotion";
import type { TextProps } from "../../_base/_core/Text/Text.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { SubscriptionIcon } from "../../_base/icons/Icon/GeneralIcons";
import type { IconElement } from "../../_base/icons/Icon/Icon";
import { InlineIcon } from "../../_base/icons/Icon/Icon";
import { HallowImage } from "../../blocks/other/HallowImage/HallowImage";

const hallowElement: HallowElement = "div";

const actionsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};
const descriptionStyle: Interpolation = {
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  color: constantSemanticColors.onColorHighest,
  display: "-webkit-box",
  overflow: "hidden",
};
const determinedElementStyle: Interpolation<{ color: BlockProps["color"] }> = (
  props,
) => ({
  backgroundColor: props.color,
  borderRadius: radius.l,
  display: "flex",
  height: "480px",
  width: spacing.full,
});
const infoContainerStyle: Interpolation = {
  borderBottomLeftRadius: radius.l,
  borderTopLeftRadius: radius.l,
  color: colors.white,
  display: "flex",
  flex: 2,
  flexDirection: "column",
  gap: spacing.s,
  justifyContent: "center",
  minWidth: "348px",
  paddingLeft: "80px",
};
const imgStyle: Interpolation = {
  WebkitUserDrag: "none",
  borderBottomRightRadius: radius.l,
  borderTopRightRadius: radius.l,
  height: spacing.full,
  maxWidth: spacing.full,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  userSelect: "none",
  width: spacing.full,
};
const imgContainerStyle: Interpolation = {
  flex: 3,
  height: spacing.full,
  position: "relative",
};
const maskStyle: Interpolation = {
  left: -1,
  position: "absolute",
};
const metadataContainerStyle: Interpolation = {
  alignItems: "center",
  color: constantSemanticColors.onColorVeryHigh,
  display: "flex",
  gap: spacing.xs,
};
const tagsContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};
const titleContainerStyle: Interpolation = {
  display: "inline-block",
  maxWidth: "480px",
};
const iconContainerStyle: Interpolation = {
  flexShrink: 0,
  marginRight: spacing.xxs,
};
const tagWidthFitStyle: Interpolation = {
  width: "fit-content !important",
};

export type BlockProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  actions?: KeyedElement[];
  actionsCSS?: Interpolation;
  activeState?: boolean;
  color?: MaskProps["color"];
  description?: ReactNode;
  descriptionCSS?: Interpolation;
  descriptionProps?: TextProps;
  focusState?: boolean;
  hoverState?: boolean;
  imgContainerProps?: HallowElementProps<"div">;
  imgContainerCSS?: Interpolation;
  imgSrc?: Images;
  metadata?: string;
  metadataInlineIcon?: IconElement;
  subscription?: boolean;
  tags?: string[];
  title?: string;
};

export const Block = forwardRef<any, BlockProps>(
  (
    {
      actions,
      actionsCSS = {},
      activeState = false,
      asChild = false,
      children,
      color = "black",
      description,
      descriptionCSS = {},
      descriptionProps,
      focusState = true,
      hoverState = true,
      imgContainerProps,
      imgContainerCSS = {},
      imgSrc,
      metadata,
      metadataInlineIcon,
      subscription,
      tags,
      title,
      ...props
    },
    ref,
  ) => {
    const theme = useThemeValues();
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div key="infoContainer" css={infoContainerStyle}>
          {title && (
            <Text size="l" type="headline" css={titleContainerStyle}>
              {subscription && (
                <SubscriptionIcon.Filled css={iconContainerStyle} />
              )}
              {title}
            </Text>
          )}
          {tags?.length > 0 && (
            <div css={tagsContainerStyle}>
              {tags?.map((tag, index) => (
                <Tag
                  key={`${tag}${index}`}
                  css={tagWidthFitStyle}
                  variant="lighten"
                >
                  {tag}
                </Tag>
              ))}
            </div>
          )}
          {description && (
            <Text
              css={[descriptionStyle, descriptionCSS]}
              {...({
                size: "m",
                type: "body",
                ...descriptionProps,
              } as TextProps)}
            >
              {description}
            </Text>
          )}
          {metadata && (
            <div css={metadataContainerStyle}>
              {metadataInlineIcon && <InlineIcon icon={metadataInlineIcon} />}
              <Text overflow="ellipsis" size="l" type="detail">
                {metadata}
              </Text>
            </div>
          )}
          {actions?.length > 0 && (
            <div css={[actionsStyle, actionsCSS]}>{actions}</div>
          )}
        </div>,
        <div
          key="imageContainer"
          {...imgContainerProps}
          css={[imgContainerStyle, imgContainerCSS]}
        >
          {imgContainerProps?.children}
          <Mask
            color={color}
            direction="to right"
            orientation="vertical"
            css={maskStyle}
          />
          {imgSrc && (
            <HallowImage size={"l"} alt={title} src={imgSrc} css={imgStyle} />
          )}
        </div>,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        {...props}
        css={[
          determinedElementStyle({
            color: theme?.semanticColors?.[color] ?? colors[color] ?? color,
          }),
          activeState ? stylesOpacity.active : null,
          activeState ? stylesOpacity.base : null,
          stylesOutline.base,
          focusState ? stylesOutline.focus : null,
          hoverState ? stylesOutline.hover : null,
          activeState && hoverState ? stylesScale.base : null,
          activeState && hoverState ? stylesScale.s : null,
        ]}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

Block.displayName = "Block";
